import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinalCta from "@/components/FinalCta";
import { useSite } from "@/context/SiteContext";

/** Public "live quote" funnel — the actual Google Solar lookups happen in the admin panel only. */
export default function LiveQuotePage() {
  const { config, openLead, openAudit } = useSite();
  return (
    <>
      <Seo
        title="Request a free live quote | Solar Black Belt"
        description="A solar specialist reviews your building with Google Maps imagery and the latest satellite sunlight data for your exact location, then follows up with a grounded live quote. Free request."
      />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">LIVE QUOTES BY OUR ADMIN</span>
          <h1 data-testid="livequote-heading">Request a free live quote.</h1>
          <p className="lead">
            Send us your address and a solar specialist follows up with a live quote built from the latest data: real Google
            Maps imagery of your building, and the sunlight-coverage history of your exact location — so what's possible is
            grounded in your property, not a guess.
          </p>
          <div style={{ display: "flex", gap: 14, marginTop: 26, flexWrap: "wrap" }}>
            <button className="btn lime" data-testid="livequote-request-btn" onClick={() => openLead("contact", "Live quote request")}>
              Request my free live quote <span>↗</span>
            </button>
            <button className="btn light" data-testid="livequote-upload-btn" onClick={() => openAudit()}>
              Upload my electric bill instead
            </button>
          </div>
        </div>
      </section>
      <section className="section" style={{ paddingTop: 44 }}>
        <div className="wrap">
          <div className="process-grid">
            <article className="process-card" data-testid="livequote-step-1">
              <span className="process-num">01</span>
              <h3>Send your address</h3>
              <p>Thirty seconds, no payment. An electric bill helps, but the address alone starts the review.</p>
            </article>
            <article className="process-card" data-testid="livequote-step-2">
              <span className="process-num">02</span>
              <h3>We inspect your building</h3>
              <p>Our admin studies Google Maps imagery of your roof and the latest satellite sunlight data for your location — the same data Google uses for its own solar estimates.</p>
            </article>
            <article className="process-card" data-testid="livequote-step-3">
              <span className="process-num">03</span>
              <h3>A specialist follows up</h3>
              <p>You get a grounded follow-up by phone or email: what fits your roof, what it could offset, and your options — commercial or home, solar plus Tesla Powerwall.</p>
            </article>
          </div>
          <p className="micro" data-testid="livequote-coverage-note">
            Honest caveat: this uses Google's solar imagery, so coverage varies by building. If your roof isn't covered yet,
            we'll say so and switch to a bill-based review instead — we never invent numbers. Requests route to our admin team
            at {config.phone}.
          </p>
          <Reveal delay={0.06}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 18 }}>
              <Link className="btn light" to="/projects">See jobs we've done ↗</Link>
              <Link className="btn light" to="/blog">Read the solar math for your city ↗</Link>
            </div>
          </Reveal>
        </div>
      </section>
      <FinalCta title={["Your building, inspected for free."]} service="commercial" context="Live quote request" />
    </>
  );
}
